import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  Copy, Check, ExternalLink, Sparkles, CheckCircle, Clock, XCircle, Lock, CreditCard,
  TrendingUp, Zap, Star, Award, Rocket, Package, Activity, BarChart3,
  MessageCircle, Globe, Target, PlayCircle, DollarSign, ShoppingBag
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from '../../hooks/use-toast';
import QuickActions from '../dashboard/QuickActions';
import RecentActivity from '../dashboard/RecentActivity';
import ReferralCard from '../dashboard/ReferralCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Overview = ({ user, onRefresh, onSetTab, onOpenOnboarding, refreshKey = 0 }) => {
  const navigate = useNavigate();
  const [onboardingData, setOnboardingData] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [whatsappNumber, setWhatsappNumber] = useState('918965883955');
  const [connectingWhatsapp, setConnectingWhatsapp] = useState(false);

  const fetchOnboardingData = async () => {
    try {
      const response = await axios.get(`${API}/onboarding/${user.principalId}`);
      setOnboardingData(response.data);
    } catch (error) {
      console.error('Error fetching onboarding data:', error);
    }
  };

  const fetchSupportNumber = async () => {
    try {
      const response = await axios.get(`${API}/payment-settings`);
      if (response.data?.whatsappNumber) {
        setWhatsappNumber(response.data.whatsappNumber);
      }
    } catch (error) {
      console.error('Error fetching support number:', error);
    }
  };

  useEffect(() => {
    if (user?.principalId) {
      fetchOnboardingData();
      fetchSupportNumber();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, refreshKey]);

  const handleConnectWhatsapp = async () => {
    setConnectingWhatsapp(true);
    const phone = (whatsappNumber || '918965883955').replace(/[^0-9]/g, '');
    const greeting = encodeURIComponent(
      `Hi DropSquadz! I'm ${user?.name || 'a new client'} (Client ID: ${user?.clientId || user?.principalId}). I'd like to connect for onboarding support.`
    );
    // Open WhatsApp chat in a new tab
    window.open(`https://api.whatsapp.com/send/?phone=${phone}&text=${greeting}`, '_blank');
    try {
      await axios.post(`${API}/onboarding/whatsapp-connect`, { userId: user.principalId });
      await fetchOnboardingData();
      toast({
        title: 'WhatsApp Connected',
        description: "We've marked your WhatsApp step as completed.",
      });
    } catch (error) {
      console.error('WhatsApp connect error:', error);
      toast({
        title: 'Connection failed',
        description: 'Please try again or contact support.',
        variant: 'destructive',
      });
    } finally {
      setConnectingWhatsapp(false);
    }
  };

  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text);
    setCopiedId(type);
    toast({
      title: 'Copied!',
      description: `${type} copied to clipboard`,
    });
    setTimeout(() => setCopiedId(null), 2000);
  };

  const getStepStatus = (step) => {
    if (!onboardingData) return 'Not Started';
    return onboardingData[step] || 'Not Started';
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'Completed':
        return 'bg-green-100 text-green-700 border-green-200';
      case 'Pending':
        return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'Live':
        return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'Over':
        return 'bg-red-100 text-red-700 border-red-200';
      case 'Approved':
        return 'bg-green-100 text-green-700 border-green-200';
      default:
        return 'bg-slate-100 text-slate-600 border-slate-200';
    }
  };

  // NEW Onboarding Steps as per user request:
  // 1. Connect on WhatsApp
  // 2. Website Creation
  // 3. Ads Campaign Setup
  // 4. Ads Budget Payment (after admin approval)
  // 5. Ads Status
  // Step 1 status is derived: complete only when the wizard's WhatsApp step is done.
  const whatsappStatus = getStepStatus('whatsappStatus');
  const startOnboardingStatus = (getStepStatus('startWorkStatus') === 'Completed' || whatsappStatus === 'Completed') ? 'Completed' : 'Not Started';
  const onboardingSteps = [
    {
      id: 'startOnboarding',
      step: 1,
      title: 'Start Onboarding',
      description: 'Choose products, share Shopify, set ads budget & connect on WhatsApp',
      status: startOnboardingStatus,
      icon: Rocket,
      field: startOnboardingStatus === 'Completed' && onboardingData?.whatsappConnectedAt ? (
        <div className="mt-2 text-xs text-slate-600">
          <span className="text-slate-500">Completed:</span>
          <span className="ml-2 text-green-600 font-medium">
            {new Date(onboardingData.whatsappConnectedAt).toLocaleDateString()}
          </span>
        </div>
      ) : null,
      action: startOnboardingStatus !== 'Completed' ? (
        <Button
          size="sm"
          onClick={() => onOpenOnboarding?.()}
          data-testid="start-onboarding-btn"
          className="bg-blue-600 hover:bg-blue-700 text-white shadow-lg active:scale-95"
        >
          <Rocket className="w-4 h-4 mr-2" />
          Start Onboarding
        </Button>
      ) : null
    },
    {
      id: 'websiteStatus',
      step: 2,
      title: 'Website Creation',
      description: 'Your Shopify store is being created by our team',
      status: getStepStatus('websiteStatus'),
      icon: Globe,
      field: onboardingData?.websiteLink && onboardingData?.websiteStatus === 'Completed' ? (
        <a 
          href={onboardingData.websiteLink} 
          target="_blank" 
          rel="noopener noreferrer" 
          className="text-sm text-blue-600 hover:text-blue-700 hover:underline flex items-center space-x-1 mt-2"
        >
          <ExternalLink className="w-4 h-4" />
          <span>{onboardingData.websiteLink}</span>
        </a>
      ) : null
    },
    {
      id: 'campaignStatus',
      step: 3,
      title: 'Ads Campaign Setup',
      description: 'Facebook & Instagram ads are being configured',
      status: getStepStatus('campaignStatus'),
      icon: Target,
    },
    {
      id: 'adsBudgetStatus',
      step: 4,
      title: 'Ads Budget Payment',
      description: 'Pay ads budget after admin verification to go live',
      status: getStepStatus('adsBudgetStatus') || 'Not Started',
      icon: CreditCard,
      requiresApproval: true,
      action: onboardingData?.campaignStatus === 'Completed' && getStepStatus('adsBudgetStatus') !== 'Completed' ? (
        onboardingData?.adsBudgetApproved ? (
          <Button
            size="sm"
            onClick={() => navigate('/ads-budget-payment', { 
              state: { 
                userId: user.principalId, 
                budget: onboardingData?.adsBudget || 500,
                days: 7 
              } 
            })}
            className="bg-green-600 hover:bg-green-700 text-white shadow-lg"
          >
            <CreditCard className="w-4 h-4 mr-2" />
            Pay Ads Budget
          </Button>
        ) : (
          <Badge className="bg-yellow-100 text-yellow-700 border-yellow-200">
            <Clock className="w-3 h-3 mr-1" />
            Awaiting Admin Approval
          </Badge>
        )
      ) : null
    },
    {
      id: 'adsStatus',
      step: 5,
      title: 'Ads Status',
      description: 'Your advertising campaigns status',
      status: getStepStatus('adsStatus'),
      icon: PlayCircle,
      field: onboardingData?.adsStatus === 'Live' || onboardingData?.adsStatus === 'Over' ? (
        <div className="mt-3 space-y-2">
          {onboardingData?.adsLiveDate && (
            <div className="text-xs text-slate-600">
              <span className="text-slate-500">Live Since:</span>
              <span className="ml-2 text-green-600 font-medium">{new Date(onboardingData.adsLiveDate).toLocaleDateString()}</span>
            </div>
          )}
          {onboardingData?.adsOverDate && (
            <div className="text-xs text-slate-600">
              <span className="text-slate-500">Ends On:</span>
              <span className="ml-2 text-red-600 font-medium">{new Date(onboardingData.adsOverDate).toLocaleDateString()}</span>
            </div>
          )}
          {onboardingData?.adsStatus === 'Over' && (
            <div className="p-2 bg-red-50 border border-red-200 rounded text-xs text-red-600 font-medium">
              Contact Agent to Reactivate Ads
            </div>
          )}
        </div>
      ) : null
    }
  ];

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Welcome Card */}
      <Card className="bg-gradient-to-r from-blue-600 to-blue-700 border-0 text-white overflow-hidden relative">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />
        <CardContent className="p-4 md:p-6 relative z-10">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h2 className="text-xl sm:text-2xl font-bold mb-1">
                Welcome back, {user?.name?.split(' ')[0] || 'User'}! 👋
              </h2>
              <p className="text-blue-100">
                {user?.activePlan 
                  ? `You're on the ${user.activePlan} plan` 
                  : 'Complete your onboarding to get started'}
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <div className="bg-white/20 rounded-lg px-4 py-2">
                <p className="text-xs text-blue-100">Client ID</p>
                <div className="flex items-center space-x-2">
                  <p className="font-mono font-bold">#{user?.clientId}</p>
                  <button onClick={() => copyToClipboard(user?.clientId?.toString(), 'Client ID')}>
                    {copiedId === 'Client ID' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Selected Data Summary Card */}
      {(user?.activePlan || onboardingData) && (
        <Card className="bg-white border-slate-200 shadow-lg">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg text-slate-800 flex items-center">
              <ShoppingBag className="w-5 h-5 mr-2 text-blue-600" />
              Your Selection Summary
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4">
              {/* Active Plan */}
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-3 sm:p-4 border border-blue-200 min-w-0">
                <div className="flex items-center space-x-2 mb-2">
                  <Package className="w-4 h-4 text-blue-600" />
                  <p className="text-xs text-slate-500 font-medium">Active Plan</p>
                </div>
                <p className="text-base sm:text-xl font-bold truncate text-blue-600">{user?.activePlan || 'None'}</p>
              </div>
              
              {/* Products Count */}
              <div className="bg-gradient-to-br from-green-50 to-emerald-100 rounded-xl p-3 sm:p-4 border border-green-200 min-w-0">
                <div className="flex items-center space-x-2 mb-2">
                  <ShoppingBag className="w-4 h-4 text-green-600" />
                  <p className="text-xs text-slate-500 font-medium">Products</p>
                </div>
                <p className="text-base sm:text-xl font-bold truncate text-green-600">{onboardingData?.selectedProducts?.length || 0}</p>
              </div>
              
              {/* Ads Budget */}
              <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-3 sm:p-4 border border-purple-200 min-w-0">
                <div className="flex items-center space-x-2 mb-2">
                  <DollarSign className="w-4 h-4 text-purple-600" />
                  <p className="text-xs text-slate-500 font-medium">Ads Budget</p>
                </div>
                <p className="text-base sm:text-xl font-bold truncate text-purple-600">₹{Number((onboardingData?.adsBudget && typeof onboardingData.adsBudget === 'object' ? (onboardingData.adsBudget.amount || onboardingData.adsBudget.total) : onboardingData?.adsBudget) || onboardingData?.adsBudgetSelected || 0).toLocaleString('en-IN')}/day</p>
              </div>
              
              {/* Ads Status */}
              <div className="bg-gradient-to-br from-orange-50 to-amber-100 rounded-xl p-3 sm:p-4 border border-orange-200 min-w-0">
                <div className="flex items-center space-x-2 mb-2">
                  <Target className="w-4 h-4 text-orange-600" />
                  <p className="text-xs text-slate-500 font-medium">Ads Status</p>
                </div>
                <p className="text-base sm:text-xl font-bold truncate text-orange-600">{onboardingData?.adsStatus || 'Not Started'}</p>
              </div>
            </div>

            {/* Selected Products List */}
            {onboardingData?.selectedProducts && onboardingData.selectedProducts.length > 0 && (
              <div className="mt-4 pt-4 border-t border-slate-200">
                <p className="text-sm font-medium text-slate-700 mb-3">Selected Products:</p>
                <div className="flex flex-wrap gap-2">
                  {onboardingData.selectedProducts.map((product, idx) => (
                    <Badge key={idx} className="bg-blue-100 text-blue-700 border-blue-200 px-3 py-1">
                      {product.name || product}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Website Link */}
            {onboardingData?.websiteLink && (
              <div className="mt-4 pt-4 border-t border-slate-200">
                <p className="text-sm font-medium text-slate-700 mb-2">Your Website:</p>
                <a 
                  href={onboardingData.websiteLink} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-blue-600 hover:text-blue-700 text-sm bg-blue-50 px-3 py-2 rounded-lg border border-blue-200"
                >
                  <Globe className="w-4 h-4 mr-2" />
                  {onboardingData.websiteLink}
                  <ExternalLink className="w-3 h-3 ml-2" />
                </a>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Onboarding Progress */}
      <Card className="bg-white border-slate-200 shadow-lg">
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="text-lg text-slate-800 flex items-center">
              <Rocket className="w-5 h-5 mr-2 text-blue-600" />
              Onboarding Progress
            </span>
            {user?.activePlan && (
              <Badge className="bg-blue-600 text-white border-0">
                {user.activePlan} Plan
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {onboardingSteps.map((step, index) => {
              const Icon = step.icon;
              const isCompleted = step.status === 'Completed' || step.status === 'Live';
              const isPending = step.status === 'Pending';
              
              return (
                <div 
                  key={step.id}
                  className={`relative p-4 rounded-xl border-2 transition-all ${
                    isCompleted 
                      ? 'bg-green-50 border-green-200' 
                      : isPending 
                        ? 'bg-yellow-50 border-yellow-200'
                        : 'bg-slate-50 border-slate-200'
                  }`}
                  data-testid={`onboarding-step-${step.id}`}
                >
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                    <div className="flex items-start space-x-3 sm:space-x-4 min-w-0 flex-1">
                      {/* Step Number & Icon */}
                      <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center shadow-md flex-shrink-0 ${
                        isCompleted 
                          ? 'bg-green-500 text-white' 
                          : isPending 
                            ? 'bg-yellow-500 text-white'
                            : 'bg-slate-200 text-slate-500'
                      }`}>
                        {isCompleted ? (
                          <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6" />
                        ) : (
                          <Icon className="w-5 h-5 sm:w-6 sm:h-6" />
                        )}
                      </div>
                      
                      {/* Step Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mb-1">
                          <h4 className="font-semibold text-slate-800 text-sm sm:text-base">
                            Step {step.step}: {step.title}
                          </h4>
                          <Badge className={`text-[10px] sm:text-xs ${getStatusBadgeColor(step.status)}`}>
                            {step.status}
                          </Badge>
                        </div>
                        <p className="text-xs sm:text-sm text-slate-500">{step.description}</p>
                        
                        {/* Custom Field */}
                        {step.field}
                      </div>
                    </div>
                    
                    {/* Action Button */}
                    {step.action && (
                      <div className="flex-shrink-0 sm:ml-2 w-full sm:w-auto">
                        {step.action}
                      </div>
                    )}
                  </div>
                  
                  {/* Connection Line */}
                  {index < onboardingSteps.length - 1 && (
                    <div className={`hidden sm:block absolute left-9 top-16 w-0.5 h-8 ${
                      isCompleted ? 'bg-green-300' : 'bg-slate-200'
                    }`} />
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4">
        <Card className="bg-white border-slate-200 shadow-sm hover:shadow-md transition-all">
          <CardContent className="p-4 text-center">
            <TrendingUp className="w-8 h-8 mx-auto mb-2 text-blue-500" />
            <p className="text-2xl font-bold text-slate-800">₹0</p>
            <p className="text-xs text-slate-500">Total Revenue</p>
          </CardContent>
        </Card>
        <Card className="bg-white border-slate-200 shadow-sm hover:shadow-md transition-all">
          <CardContent className="p-4 text-center">
            <Package className="w-8 h-8 mx-auto mb-2 text-green-500" />
            <p className="text-2xl font-bold text-slate-800">0</p>
            <p className="text-xs text-slate-500">Total Orders</p>
          </CardContent>
        </Card>
        <Card className="bg-white border-slate-200 shadow-sm hover:shadow-md transition-all">
          <CardContent className="p-4 text-center">
            <BarChart3 className="w-8 h-8 mx-auto mb-2 text-purple-500" />
            <p className="text-2xl font-bold text-slate-800">0</p>
            <p className="text-xs text-slate-500">Active Ads</p>
          </CardContent>
        </Card>
        <Card className="bg-white border-slate-200 shadow-sm hover:shadow-md transition-all">
          <CardContent className="p-4 text-center">
            <Star className="w-8 h-8 mx-auto mb-2 text-yellow-500" />
            <p className="text-2xl font-bold text-slate-800">0</p>
            <p className="text-xs text-slate-500">Earnings</p>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions Widget */}
      <QuickActions onAction={onSetTab} />

      {/* Refer & Earn Card */}
      <ReferralCard user={user} />

      {/* Recent Activity Widget – auto-hides when empty (see RecentActivity.jsx) */}
      <RecentActivity userId={user?.principalId} hideWhenEmpty />

      {/* Help Section */}
      <Card className="bg-gradient-to-r from-blue-50 to-blue-100 border-blue-200">
        <CardContent className="p-4">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center shadow-lg">
                <MessageCircle className="w-5 h-5 text-white" />
              </div>
              <div>
                <h4 className="font-semibold text-slate-800">Need Help?</h4>
                <p className="text-sm text-slate-600">Our support team is available 24/7</p>
              </div>
            </div>
            <Button
              onClick={() => window.open('https://api.whatsapp.com/send/?phone=918965883955&text=Help+needed', '_blank')}
              className="bg-green-500 hover:bg-green-600 text-white shadow-lg"
            >
              <MessageCircle className="w-4 h-4 mr-2" />
              Chat on WhatsApp
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Overview;
